
































































































































import { CreateElement, VNode } from 'vue';
import { Component, Prop, Inject } from 'vue-property-decorator';
import { SwExtendedVue } from '../sw-extended-vue';
import { SwIcon } from '../sw-icon';
import { drag, DragPosition } from '../drag-dir';
import { TitlePaneTheme } from './types';
import SwTitlePaneGroup from './sw-title-pane-group.vue';
import { CSSStyleOptions } from '../common-types';
import { utilService } from '../util-service';

@Component({
  components: {
    SwIcon,
  },
  directives: {
    drag,
  },
})
export default class SwTitlePane extends SwExtendedVue {
  @Prop(String)
  paneId: string;

  @Prop({ type: String, default: 'regular' })
  type: 'regular' | 'multilevel' | 'draggable';

  @Prop(String)
  titleName: string;

  @Prop({ type: String, default: 'light' })
  theme: TitlePaneTheme;

  @Prop({ type: Boolean, default: false })
  indent: boolean;

  /** effective only if `type === multilevel` */
  @Prop({ type: Number, default: 1 })
  level: number;

  /** effective only if `type === draggable` */
  @Prop({ type: Boolean, default: false })
  noDrag: boolean;

  /** effective only if `type === draggable` */
  @Prop({ type: String, default: 'any' })
  dragDirection: 'any' | 'vertical' | 'horizontal';

  /** effective only if `type === draggable` */
  @Prop({ type: Boolean, default: false })
  backToOriginal: boolean;

  /** effective only if `type === draggable` */
  @Prop({ type: Object, default() { return {}; } })
  initPosition: {left?: number, top?: number, bottom?: number, right?: number};

  @Prop({ type: Boolean, default: false })
  noBackground: boolean;

  cssPrefix: string = 'sw-title-pane';

  position: {left?: number, top?: number, bottom?: number, right?: number} = this.initPosition;

  @Inject({ default() {} })
  claimPaneInfo: SwTitlePaneGroup['claimPaneInfo'];

  @Inject({ default() {} })
  paneParam: SwTitlePaneGroup['paneParam'];

  @Inject({ default() {} })
  claimPaneDrag: SwTitlePaneGroup['claimPaneDrag'];

  get paneInfo() {
    if (this.paneParam === undefined) return undefined;
    return this.paneParam.panesInfo.find(e => (e.paneId === this.paneId));
  }

  get curTheme() {
    if (this.paneParam === undefined || this.paneInfo === undefined) {
      return this.theme;
    }
    const theme = this.paneInfo.theme;
    return theme;
  }

  get isInGroup(): boolean {
    return this.paneInfo !== undefined;
  }

  get rootStyle(): CSSStyleOptions {
    if (this.position) {
      return {
        left: this.position.left !== undefined ? `${this.position.left}px` : undefined,
        top: this.position.top !== undefined ? `${this.position.top}px` : undefined,
        bottom: this.position.bottom !== undefined ? `${this.position.bottom}px` : undefined,
        right: this.position.right !== undefined ? `${this.position.right}px` : undefined,
      };
    }
    return {};
  }

  private dragPositioning(toClientPos: DragPosition) {
    const op = (this.$el as HTMLElement).offsetParent;
    if (!op) { return; }
    const opRect = op.getBoundingClientRect();
    if (this.dragDirection === 'vertical') {
      this.position = {
        top: toClientPos.y - opRect.top,
        left: 0,
      };
    } else if (this.dragDirection === 'horizontal') {
      this.position = {
        left: toClientPos.x - opRect.left,
        top: 0,
      };
    } else {
      this.position = {
        left: toClientPos.x - opRect.left,
        top: toClientPos.y - opRect.top,
      };
    }
  }

  private dragStart() {
    // for the group drag
    if (this.paneParam !== undefined) {
      const el = this.$el as HTMLElement;
      if (el != null) {
        el.style.zIndex = '10';
        if (this.paneInfo !== undefined) {
          el.style.top = `${this.paneInfo.top}px`;
        }
        el.style.left = '0px';
        el.style.opacity = '0.7';
        el.style.position = 'absolute';
        el.style.width = '100%';
      }
    }
  }

  private dragDone() {
    if (this.backToOriginal) {
      this.position = {
        left: 0,
        top: 0,
      };
    }

    // for the group drag
    if (this.paneParam !== undefined) {
      const dragEl = this.$el as HTMLElement;

      const top = this.position.top;
      if (top !== undefined) {
        this.claimPaneDrag(this.paneId , top, dragEl);
      } else {
        // fix a bug for the top is undefined
        dragEl.style.position = 'relative';
        dragEl.style.width = '';
        dragEl.style.zIndex = '';
        dragEl.style.left = '';
        dragEl.style.top = '';
      }

      dragEl.style.opacity = '1.0';
    }

    if (this.paneInfo !== undefined) {
      this.$emit('drag-done', this.position, this.paneInfo.theme);
    } else {
      this.$emit('drag-done', this.position);
    }
  }

  mounted() {
    if (this.type === 'draggable') {
      const height = getComputedStyle(this.$el).height;
      if (height != null && this.$el !== null && this.paneId !== '' && typeof this.claimPaneInfo === 'function') {
        this.claimPaneInfo(this.paneId, this.$el as HTMLElement, Number(height.slice(0, -2)));
      }
    }
  }

  render(h: CreateElement): VNode {// tslint:disable-line
    const dirs: any[] = [];

    if (this.type === 'draggable' && !this.noDrag) {
      dirs.push({
        name: 'drag',
        value: {
          positioning: this.dragPositioning,
          start: this.dragStart,
          done: this.dragDone,
        },
      });
    }

    const headerSlot = utilService.getVueScopedSlot(this.$scopedSlots, 'header');

    const rootNode = (
      <div
        class={[
          `${this.cssPrefix}`, `${this.cssPrefix}--${this.curTheme}`, 'sw-flexbox', 'sw-flexbox--column', 'sw-typo-default',
          {
            [`${this.cssPrefix}--draggable`]: this.type === 'draggable',
            [`${this.cssPrefix}--in-group`]: this.isInGroup,
            [`${this.cssPrefix}--no-background`]: this.noBackground,
          },
        ]}
        style={this.rootStyle}
      >
        <div
          class={[
            `${this.cssPrefix}__header`,
            'sw-flexbox__flex-none',
            {
              [`${this.cssPrefix}__header--regular`]: this.type === 'regular',
              [`${this.cssPrefix}__header--level1`]: this.type === 'multilevel' && this.level === 1,
              [`${this.cssPrefix}__header--level2`]: this.type === 'multilevel' && this.level === 2,
              [`${this.cssPrefix}__header--draggable`]: this.type === 'draggable',
              [`${this.cssPrefix}__header--no-drag`]: this.noDrag,
              'sw-flexbox': this.type === 'multilevel' && this.level === 2,
              'sw-flexbox--center-items': this.type === 'multilevel' && this.level === 2,
            },
          ]}
          {...{ directives: dirs }}
        >
          <div
            class={[
              `${this.cssPrefix}__header-content`,
              'sw-flexbox__flex', 'sw-flexbox', 'sw-flexbox--center-items',
              { [`${this.cssPrefix}__header-content--indent`]: this.indent },
            ]}
          >
            {
              this.type === 'draggable' ?
                <div class={[`${this.cssPrefix}__header-icon`, 'sw-flexbox__flex-none'] }>
                  <SwIcon name="dragdable-handle"
                    props-size={16}
                    props-as-block={true}
                  ></SwIcon>
                </div> : undefined
            }
            <div class={[`${this.cssPrefix}__title-cont`, 'sw-flexbox__flex']}>{
              headerSlot.length > 0 ? headerSlot : this.titleName
            }</div>
          </div>
        </div>
        <div
          class={[
            `${this.cssPrefix}__main`,
            'sw-flexbox__flex-none',
            { [`${this.cssPrefix}__main--indent`]: this.indent },
          ]}
        >{utilService.getVueScopedSlot(this.$scopedSlots)}</div>
      </div>
    );
    return rootNode as VNode;
  }
}
